import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, pdf } from '@react-pdf/renderer';
import { TermsAndConditions, SignatureData } from '../types';

interface PurchaseItem {
  fee_name: string;
  fee_category: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  partial_payment_amount?: number;
  questionnaire_responses?: any;
}

interface PurchaseData {
  id: string;
  confirmation_number: string;
  created_at: string;
  subtotal: number;
  tax: number;
  total: number;
  status: string;
  purchase_items: PurchaseItem[];
}

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 9,
  },
  header: {
    marginBottom: 20,
    textAlign: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  feeName: {
    fontSize: 11,
    color: '#333',
    marginBottom: 3,
  },
  courseInfo: {
    fontSize: 10,
    color: '#666',
    marginBottom: 15,
  },
  infoSection: {
    marginBottom: 15,
    padding: 10,
    backgroundColor: '#f9f9f9',
    border: '1pt solid #e0e0e0',
    borderRadius: 3,
  },
  infoLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#666',
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 9,
    color: '#333',
    marginBottom: 6,
  },
  contentBox: {
    padding: 12,
    backgroundColor: '#fafafa',
    border: '1pt solid #ddd',
    borderRadius: 3,
    marginBottom: 15,
  },
  heading: {
    fontSize: 9,
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 5,
  },
  subheading: {
    fontSize: 8,
    fontWeight: 'bold',
    marginTop: 8,
    marginBottom: 4,
  },
  paragraph: {
    fontSize: 8,
    lineHeight: 1.4,
    marginBottom: 4,
  },
  listItem: {
    fontSize: 8,
    lineHeight: 1.4,
    marginLeft: 12,
    marginBottom: 2,
  },
  divider: {
    borderBottom: '1pt solid #ccc',
    marginVertical: 12,
  },
  signatureSection: {
    marginTop: 15,
    padding: 12,
    backgroundColor: '#f9f9f9',
    border: '1pt solid #e0e0e0',
    borderRadius: 3,
  },
  signatureTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  signatureInfo: {
    fontSize: 9,
    marginBottom: 5,
  },
  signatureLabel: {
    fontSize: 8,
    color: '#666',
    marginTop: 10,
    marginBottom: 5,
  },
  signatureImage: {
    width: 180,
    height: 55,
    marginBottom: 3,
    border: '1pt solid #ccc',
    borderRadius: 2,
  },
  signatureLine: {
    width: 180,
    borderBottom: '1pt solid #999',
    marginBottom: 10,
  },
  footer: {
    fontSize: 7,
    color: '#999',
    marginTop: 8,
    fontStyle: 'italic',
  },
  receiptPage: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    backgroundColor: '#ffffff',
  },
  receiptHeader: {
    marginBottom: 30,
    paddingBottom: 20,
    borderBottom: '2pt solid #0f766e',
  },
  brandSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  brandName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0f766e',
    marginBottom: 5,
  },
  receiptTitle: {
    fontSize: 16,
    color: '#666',
    marginBottom: 5,
  },
  orderNumber: {
    fontSize: 12,
    color: '#333',
    marginBottom: 2,
  },
  orderDate: {
    fontSize: 10,
    color: '#666',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#0f766e',
    marginTop: 20,
    marginBottom: 10,
    paddingBottom: 5,
    borderBottom: '1pt solid #e0e0e0',
  },
  itemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: '#fafafa',
    marginBottom: 8,
    borderRadius: 3,
  },
  itemName: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 3,
    flex: 1,
  },
  itemDetails: {
    fontSize: 9,
    color: '#666',
    marginBottom: 2,
  },
  itemPrice: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#0f766e',
    textAlign: 'right',
    minWidth: 80,
  },
  summarySection: {
    marginTop: 20,
    paddingTop: 15,
    borderTop: '2pt solid #e0e0e0',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  summaryLabel: {
    fontSize: 10,
    color: '#666',
  },
  summaryValue: {
    fontSize: 10,
    color: '#333',
    fontWeight: 'bold',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 10,
    marginTop: 8,
    backgroundColor: '#0f766e',
    borderRadius: 3,
  },
  totalLabel: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  totalValue: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  receiptFooter: {
    marginTop: 30,
    paddingTop: 15,
    borderTop: '1pt solid #e0e0e0',
    textAlign: 'center',
  },
  footerText: {
    fontSize: 8,
    color: '#999',
    marginBottom: 3,
  },
  partialPaymentBadge: {
    backgroundColor: '#fff3cd',
    padding: 5,
    borderRadius: 2,
    marginTop: 3,
  },
  partialPaymentText: {
    fontSize: 8,
    color: '#856404',
  },
});

interface PDFDocumentProps {
  feeName: string;
  terms: TermsAndConditions;
  signatureData: SignatureData;
  course?: string;
}

const SignedAgreementDocument: React.FC<PDFDocumentProps> = ({ feeName, terms, signatureData, course }) => {
  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];

    lines.forEach((line, index) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        const text = line.slice(2, -2);
        if (text.includes(':')) {
          elements.push(
            <Text key={index} style={styles.subheading}>
              {text}
            </Text>
          );
        } else {
          elements.push(
            <Text key={index} style={styles.heading}>
              {text}
            </Text>
          );
        }
      } else if (line.startsWith('- ') || line.match(/^\d+\./)) {
        elements.push(
          <Text key={index} style={styles.listItem}>
            {line}
          </Text>
        );
      } else if (line.trim() !== '') {
        elements.push(
          <Text key={index} style={styles.paragraph}>
            {line}
          </Text>
        );
      }
    });

    return elements;
  };

  const formattedDate = new Date(signatureData.signedAt).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>{terms.title}</Text>
          <Text style={styles.feeName}>{feeName}</Text>
          {course && <Text style={styles.courseInfo}>{course}</Text>}
        </View>

        <View style={styles.infoSection}>
          <Text style={styles.infoLabel}>Event/Activity Details:</Text>
          <Text style={styles.infoValue}>
            <Text style={{ fontWeight: 'bold' }}>Name:</Text> {feeName}
          </Text>
          {course && (
            <Text style={styles.infoValue}>
              <Text style={{ fontWeight: 'bold' }}>Course:</Text> {course}
            </Text>
          )}
        </View>

        <View style={styles.contentBox}>
          {renderContent(terms.content)}
        </View>

        <View style={styles.divider} />

        <View style={styles.signatureSection}>
          <Text style={styles.signatureTitle}>SIGNATURE AND ACCEPTANCE</Text>

          <Text style={styles.signatureInfo}>
            <Text style={{ fontWeight: 'bold' }}>Accepted by:</Text> {signatureData.signedBy}
          </Text>

          <Text style={styles.signatureInfo}>
            <Text style={{ fontWeight: 'bold' }}>Date:</Text> {formattedDate}
          </Text>

          {signatureData.drawnSignature && (
            <View>
              <Text style={styles.signatureLabel}>Electronic Signature:</Text>
              <Image
                src={signatureData.drawnSignature}
                style={styles.signatureImage}
              />
              <View style={styles.signatureLine} />
            </View>
          )}

          <Text style={styles.footer}>
            This is a digitally signed document with a legal electronic signature.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export const generateSignedAgreementPDF = async (
  feeName: string,
  terms: TermsAndConditions,
  signatureData: SignatureData,
  course?: string
): Promise<void> => {
  const blob = await pdf(
    <SignedAgreementDocument
      feeName={feeName}
      terms={terms}
      signatureData={signatureData}
      course={course}
    />
  ).toBlob();

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${feeName.replace(/[^a-z0-9]/gi, '_')}_Signed_Agreement.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ReceiptDocument: React.FC<{ purchase: PurchaseData }> = ({ purchase }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCategoryName = (category: string) => {
    return category
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Document>
      <Page size="A4" style={styles.receiptPage}>
        <View style={styles.receiptHeader}>
          <View style={styles.brandSection}>
            <Text style={styles.brandName}>Fee Management System</Text>
          </View>
          <Text style={styles.receiptTitle}>Payment Receipt</Text>
          <Text style={styles.orderNumber}>Order #{purchase.confirmation_number}</Text>
          <Text style={styles.orderDate}>{formatDate(purchase.created_at)}</Text>
        </View>

        <Text style={styles.sectionTitle}>Order Details</Text>

        {purchase.purchase_items.map((item, index) => (
          <View key={index} style={styles.itemRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.itemName}>{item.fee_name}</Text>
              <Text style={styles.itemDetails}>
                Category: {formatCategoryName(item.fee_category)}
              </Text>
              <Text style={styles.itemDetails}>
                Quantity: {item.quantity} x ${item.unit_price.toFixed(2)}
              </Text>
              {item.questionnaire_responses?.ticketType && (
                <Text style={styles.itemDetails}>
                  Ticket Type: {item.questionnaire_responses.ticketType}
                </Text>
              )}
              {item.partial_payment_amount && item.partial_payment_amount < item.total_price && (
                <View style={styles.partialPaymentBadge}>
                  <Text style={styles.partialPaymentText}>
                    Partial Payment: ${item.partial_payment_amount.toFixed(2)} of ${item.total_price.toFixed(2)}
                  </Text>
                  <Text style={styles.partialPaymentText}>
                    Balance Due: ${(item.total_price - item.partial_payment_amount).toFixed(2)}
                  </Text>
                </View>
              )}
            </View>
            <Text style={styles.itemPrice}>
              ${(item.partial_payment_amount && item.partial_payment_amount < item.total_price
                ? item.partial_payment_amount
                : item.total_price
              ).toFixed(2)}
            </Text>
          </View>
        ))}

        <View style={styles.summarySection}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Subtotal:</Text>
            <Text style={styles.summaryValue}>${purchase.subtotal.toFixed(2)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Tax (7%):</Text>
            <Text style={styles.summaryValue}>${purchase.tax.toFixed(2)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Total Paid:</Text>
            <Text style={styles.totalValue}>${purchase.total.toFixed(2)}</Text>
          </View>
        </View>

        <View style={styles.receiptFooter}>
          <Text style={styles.footerText}>Thank you for your payment!</Text>
          <Text style={styles.footerText}>
            This receipt was generated on {new Date().toLocaleDateString('en-US')}
          </Text>
          <Text style={styles.footerText}>
            For questions or concerns, please contact your school administration.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export const generateReceiptPDF = async (purchase: PurchaseData): Promise<void> => {
  const blob = await pdf(<ReceiptDocument purchase={purchase} />).toBlob();

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Receipt_${purchase.confirmation_number}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
